// Step 4 — Contacts. Add / Edit / Remove + sort + filter + count.

import { useMemo, useState } from "react";
import Swal from "sweetalert2";

import { useFormStore } from "../../store";
import type { ContactRow } from "../../types";

import { openContactModal } from "./modals/contactModal";

type SortField = "name" | "title" | "email";
type SortDir = "asc" | "desc";

function fullName(c: ContactRow): string {
  return [c.new_firstname, c.new_lastname].filter(Boolean).join(" ");
}

function valueFor(c: ContactRow, field: SortField): string {
  switch (field) {
    case "name":
      return fullName(c);
    case "title":
      return c.new_title ?? "";
    case "email":
      return c.new_email ?? "";
  }
}

function compare(a: ContactRow, b: ContactRow, field: SortField): number {
  return valueFor(a, field).localeCompare(valueFor(b, field), undefined, { sensitivity: "base" });
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sort: { field: SortField; dir: SortDir };
  onChange: (next: { field: SortField; dir: SortDir }) => void;
}

function SortableHeader({ field, label, sort, onChange }: SortableHeaderProps) {
  const active = sort.field === field;
  const dir = active ? sort.dir : null;
  return (
    <th scope="col">
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-none text-reset fw-bold"
        onClick={() =>
          onChange({
            field,
            dir: active && sort.dir === "asc" ? "desc" : "asc",
          })
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        <i
          className={`ms-1 fa-solid ${
            dir === "asc" ? "fa-sort-up" : dir === "desc" ? "fa-sort-down" : "fa-sort text-muted"
          }`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

export default function Step4Contacts() {
  const contacts = useFormStore((s) => s.contacts);
  const setContacts = useFormStore((s) => s.setContacts);

  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "name",
    dir: "asc",
  });
  const [filterText, setFilterText] = useState("");

  const enriched = useMemo(
    () =>
      contacts
        .map((c, index) => ({ c, index }))
        .filter(({ c }) => c.action !== "delete"),
    [contacts],
  );

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    let rows = enriched;
    if (needle) {
      rows = rows.filter(({ c }) => {
        const blob = [fullName(c), c.new_title, c.new_email, c.new_region, c.new_phone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(needle);
      });
    }
    rows = [...rows].sort(({ c: a }, { c: b }) => {
      const cmp = compare(a, b, sort.field);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [enriched, filterText, sort]);

  const handleAdd = async () => {
    const result = await openContactModal({ mode: "create" });
    if (!result) return;
    // If no other active contact is currently primary, default this one to
    // primary so the supplier always has one. The modal's own primary
    // toggle (result.is_primary) wins if explicitly set.
    const hasExistingPrimary = contacts.some(
      (c) => c.is_primary && c.action !== "delete",
    );
    const becomesPrimary = result.is_primary ?? !hasExistingPrimary;
    let next: ContactRow[] = [...contacts, { id: null, action: "create", ...result, is_primary: becomesPrimary }];
    if (becomesPrimary) {
      // Single-primary invariant — clear flag on every other active row.
      next = next.map((c, i) => (i === next.length - 1 ? c : { ...c, is_primary: false }));
    }
    setContacts(next);
  };

  const handleEdit = async (index: number) => {
    const contact = contacts[index];
    const result = await openContactModal({ mode: "edit", initial: contact });
    if (!result) return;
    let next = [...contacts];
    next[index] = {
      ...contact,
      ...result,
      action: contact.action === "create" ? "create" : "update",
    };
    // If the modal set this contact as primary, clear the flag on all others.
    // If the modal explicitly unset primary, restore the invariant by
    // shifting primary to the first remaining contact.
    if (result.is_primary === true) {
      next = next.map((c, i) => (i === index ? c : { ...c, is_primary: false }));
    } else if (result.is_primary === false && contact.is_primary) {
      const fallback = next.findIndex(
        (c, i) => i !== index && c.action !== "delete",
      );
      if (fallback >= 0) {
        next[fallback] = { ...next[fallback], is_primary: true };
      }
    }
    setContacts(next);
  };

  const handleSetPrimary = (index: number) => {
    setContacts(
      contacts.map((c, i) => ({ ...c, is_primary: i === index })),
    );
  };

  const handleRemove = async (index: number) => {
    const contact = contacts[index];
    const name = fullName(contact) || "this contact";
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Remove this contact?",
      text: name,
      showCancelButton: true,
      confirmButtonText: "Remove",
      customClass: { confirmButton: "swal-btn-danger" },
    });
    if (!confirm.isConfirmed) return;
    let next = [...contacts];
    const wasPrimary = !!contact.is_primary;
    if (contact.id == null) {
      next.splice(index, 1);
    } else {
      next[index] = { ...contact, action: "delete", is_primary: false };
    }
    // Auto-shift primary to the first remaining active contact so the
    // supplier always has one (per design spec).
    if (wasPrimary) {
      const fallback = next.findIndex((c) => c.action !== "delete");
      if (fallback >= 0) {
        next[fallback] = { ...next[fallback], is_primary: true };
        Swal.fire({
          icon: "info",
          title: "Primary contact reassigned",
          text: `${fullName(next[fallback]) || "Remaining contact"} is now the primary contact.`,
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    }
    setContacts(next);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="brand-section-heading mb-0 flex-grow-1">
          <i className="fa-solid fa-address-book" aria-hidden="true" />
          <span>
            Contacts
            <span className="badge bg-secondary ms-2 align-middle" aria-label={`${enriched.length} contacts`}>
              {enriched.length}
            </span>
          </span>
        </h2>
        <button type="button" className="btn btn-primary" onClick={handleAdd}>
          <i className="fa-solid fa-plus me-1" aria-hidden="true" /> Add Contact
        </button>
      </div>

      <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
        <div className="flex-grow-1" style={{ maxWidth: 360 }}>
          <div className="input-group input-group-sm">
            <span className="input-group-text" aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass" />
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Filter by name, title, email…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              aria-label="Filter contacts"
            />
          </div>
        </div>
      </div>

      <div className="text-end small text-muted mb-2">
        <i className="fa-solid fa-star text-warning me-2" aria-hidden="true" />
        Select who should be your company&apos;s primary point of contact.
      </div>

      <table className="table reflow-table align-middle">
        <thead>
          <tr>
            <th scope="col" className="text-center text-nowrap">
              <i className="fa-solid fa-star text-warning me-1" aria-hidden="true" />
              Primary
            </th>
            <SortableHeader field="name" label="Name" sort={sort} onChange={setSort} />
            <SortableHeader field="title" label="Title" sort={sort} onChange={setSort} />
            <SortableHeader field="email" label="Email" sort={sort} onChange={setSort} />
            <th scope="col">Phone</th>
            <th scope="col" className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan={6} className="text-muted text-center py-3">
                {filterText
                  ? "No contacts match your filter."
                  : <>No contacts yet. Click <strong>Add Contact</strong> above to create one.</>}
              </td>
            </tr>
          )}
          {visible.map(({ c, index }) => (
            <tr key={c.id ?? `new-${index}`}>
              <td data-label="Primary" className="text-center">
                <input
                  type="radio"
                  className="form-check-input"
                  name="primary-contact"
                  checked={!!c.is_primary}
                  onChange={() => handleSetPrimary(index)}
                  aria-label={`Set ${fullName(c) || "this contact"} as the primary contact`}
                />
              </td>
              <td data-label="Name">{fullName(c)}</td>
              <td data-label="Title">{c.new_title}</td>
              <td data-label="Email">
                {c.new_email ? <a href={`mailto:${c.new_email}`}>{c.new_email}</a> : null}
              </td>
              <td data-label="Phone">{c.new_phone}</td>
              <td data-label="Actions" className="text-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-1"
                  onClick={() => handleEdit(index)}
                  title="Edit"
                >
                  <i className="fa-solid fa-pen" aria-hidden="true" />
                  <span className="visually-hidden">Edit</span>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemove(index)}
                  title="Remove"
                >
                  <i className="fa-solid fa-trash-can" aria-hidden="true" />
                  <span className="visually-hidden">Remove</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
